import { z } from 'zod'
import { guidSchema } from '../../contracts'
import { AccountGuid } from '../Accounts/Account'
import { bzOptional, firstNameSchema, lastNameSchema } from '../common-schemas'
import { optionalEmailAddressValueSchema } from '../Email/EmailTypes'
import { LocationGuid } from '../Locations/Location'
import { optionalTelephoneNumberSchema } from '../PhoneNumbers/Phone'

export type AccountMatchReason = 'phone' | 'email' | 'name' | 'address'

export const CheckDuplicatesRequestSchema = z.object({
  companyGuid: z.string().uuid(),
  contactInfo: z.object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    emailAddress: optionalEmailAddressValueSchema,
    phoneNumber: optionalTelephoneNumberSchema,
  }),
  locationInfo: bzOptional(
    z.object({
      addressLine1: z.string(),
      addressLine2: bzOptional(z.string()),
      city: z.string(),
      stateAbbreviation: z.string(),
      zipCode: z.string(),
    }),
  ),
})

export type CheckDuplicatesRequest = z.infer<typeof CheckDuplicatesRequestSchema>

export const CheckDuplicatesResponseSchema = z.object({
  totalFound: z.number(),
  duplicateAccounts: z.array(
    z.object({
      accountGuid: z.string().uuid(),
      displayName: z.string(),
      referenceNumber: z.string(),
      primaryContact: bzOptional(
        z.object({
          firstName: z.string(),
          lastName: z.string(),
          emailAddress: bzOptional(z.string()),
          phoneNumber: bzOptional(z.string()),
        }),
      ),
      primaryLocation: bzOptional(
        z.object({
          locationGuid: guidSchema,
          formattedAddress: z.string(),
        }),
      ),
      matchReason: z.array(z.enum(['phone', 'email', 'name', 'address'])),
    }),
  ),
})

export type CheckDuplicatesResponse = z.infer<typeof CheckDuplicatesResponseSchema>

export type DuplicateAccountPreview = {
  accountGuid: AccountGuid
  displayName: string
  referenceNumber: string
  primaryContact?: {
    firstName: string
    lastName: string
    emailAddress?: string
    phoneNumber?: string
  }
  primaryLocation?: {
    locationGuid: LocationGuid
    formattedAddress: string
  }
  matchReason: AccountMatchReason[]
}

export const DuplicateAccountPreviewSchema = z.object({
  accountGuid: z.string().uuid(),
  displayName: z.string(),
  referenceNumber: z.string(),
  primaryContact: bzOptional(
    z.object({
      firstName: z.string(),
      lastName: z.string(),
      emailAddress: bzOptional(z.string()),
      phoneNumber: bzOptional(z.string()),
    }),
  ),
  primaryLocation: bzOptional(
    z.object({
      locationGuid: z.string().uuid(),
      formattedAddress: z.string(),
    }),
  ),
  matchReason: z.array(z.enum(['phone', 'email', 'name', 'address'])),
})
